import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex items-start justify-between mb-8 gap-4" data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{subtitle}</div>
        <h1 className="text-3xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{title}</h1>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function MetricCard({ label, value, sublabel, tone = "default", icon: Icon, testid, to }) {
  const tones = {
    default: "border-slate-200",
    primary: "border-[#111111]",
    warning: "border-amber-400",
    danger: "border-red-400",
    success: "border-emerald-400",
    gold: "border-[#B8873A]",
  };
  const iconColor = {
    default: "text-slate-400",
    primary: "text-[#111111]",
    warning: "text-amber-600",
    danger: "text-red-600",
    success: "text-emerald-600",
    gold: "text-[#B8873A]",
  }[tone];
  const clickable = Boolean(to);
  const cardClass = `metric-card bg-white p-5 rounded-sm border ${tones[tone]}${clickable ? " cursor-pointer group transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 hover:border-[#B8873A]" : ""}`;
  const inner = (
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{label}</div>
        <div className="text-3xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{value}</div>
        {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
      </div>
      <div className="flex flex-col items-end gap-2">
        {Icon && <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />}
        {clickable && <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 group-hover:text-[#B8873A] transition-opacity duration-150" />}
      </div>
    </div>
  );
  if (clickable) {
    return <Link to={to} data-testid={testid} className={cardClass}>{inner}</Link>;
  }
  return <div className={cardClass} data-testid={testid}>{inner}</div>;
}

export function Section({ title, actions, children }) {
  return (
    <section className="bg-white rounded-sm border border-slate-200 mb-6">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#111111]">{title}</h2>
        {actions}
      </div>
      <div>{children}</div>
    </section>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`bg-[#111111] text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function TextField({ label, testid, ...props }) {
  return (
    <label className="block">
      {label && <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{label}</div>}
      <input
        {...props}
        data-testid={testid}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm"
      />
    </label>
  );
}

export function TextArea({ label, testid, ...props }) {
  return (
    <label className="block">
      {label && <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{label}</div>}
      <textarea
        {...props}
        data-testid={testid}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm"
      />
    </label>
  );
}

export function SelectField({ label, options, testid, ...props }) {
  return (
    <label className="block">
      {label && <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{label}</div>}
      <select
        {...props}
        data-testid={testid}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm bg-white"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export function EmptyState({ icon: Icon, title, description, testid }) {
  return (
    <div className="text-center py-16 px-6" data-testid={testid}>
      {Icon && <Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />}
      <h3 className="text-base font-semibold text-slate-700">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
    </div>
  );
}
